import { useEffect } from 'react'
import { useStore } from '../../stores/useStore'
import { audioEngine } from '../../audio/engine'

export default function Header() {
  const transport = useStore((state) => state.transport)
  const toggleTransport = useStore((state) => state.toggleTransport)
  const toggleRecord = useStore((state) => state.toggleRecord)
  const stopTransport = useStore((state) => state.stopTransport)
  const setTempo = useStore((state) => state.setTempo)
  const setLoop = useStore((state) => state.setLoop)
  const updateMaster = useStore((state) => state.updateMaster)

  useEffect(() => {
    void audioEngine.init()
  }, [])

  useEffect(() => {
    if (transport.isPlaying) {
      void audioEngine.start({
        getState: () => {
          const state = useStore.getState()
          return {
            tracks: state.tracks,
            tempo: state.transport.tempo,
          }
        },
        onPosition: () => useStore.getState().setPosition(useStore.getState().transport.position + 1),
      })
    } else {
      audioEngine.stop()
    }
  }, [transport.isPlaying])

  useEffect(() => {
    audioEngine.setMaster(transport.masterVolume, transport.masterPan, transport.limiter)
  }, [transport.masterVolume, transport.masterPan, transport.limiter])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const isEditing = target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
      if (isEditing) return

      if (event.code === 'Space') {
        event.preventDefault()
        toggleTransport()
      }
      if (event.code === 'KeyS') {
        event.preventDefault()
        stopTransport()
      }
      if (event.code === 'KeyL') {
        event.preventDefault()
        setLoop(!transport.loop)
      }
      if (event.code === 'KeyR' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        toggleRecord()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleTransport, stopTransport, setLoop, transport.loop, toggleRecord])

  const handleTempoChange = (value: string) => {
    const next = Number(value)
    if (!Number.isNaN(next)) setTempo(next)
  }

  const handleVolume = (value: number) => updateMaster({ volume: value })
  const handlePan = (value: number) => updateMaster({ pan: value })

  return (
    <header className="daw-header">
      <div className="header-left">
        <h1>DAW Studio</h1>
        <span className="header-status">{transport.timeSignature[0]}/{transport.timeSignature[1]} {transport.loop ? 'Loop' : ''}</span>
      </div>

      <div className="transport">
        <button
          className={`btn transport-button ${transport.isPlaying ? 'is-active' : ''}`}
          onClick={toggleTransport}
          title="Play/Pause Space"
        >
          {transport.isPlaying ? 'PAUSE' : 'PLAY'}
        </button>
        <button className="btn transport-button" onClick={stopTransport} title="Stop S">STOP</button>
        <button
          className={`btn transport-button ${transport.isRecording ? 'btn-danger is-active' : ''}`}
          onClick={toggleRecord}
          title="Record"
        >
          REC
        </button>
      </div>

      <div className="header-right">
        <label className="tempo-control">
          <span>BPM</span>
          <input
            type="number"
            value={transport.tempo}
            onChange={(e) => handleTempoChange(e.target.value)}
            min="40"
            max="300"
          />
        </label>
        <label className="tempo-control" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={transport.loop}
            onChange={(e) => setLoop(e.target.checked)}
          />
          <span>Loop</span>
        </label>
        <div className="master-controls">
          <div className="control-row">
            <label>VOL</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={transport.masterVolume}
              onChange={(e) => handleVolume(parseFloat(e.target.value))}
              className="slider"
            />
          </div>
          <div className="control-row">
            <label>PAN</label>
            <input
              type="range"
              min="-1"
              max="1"
              step="0.01"
              value={transport.masterPan}
              onChange={(e) => handlePan(parseFloat(e.target.value))}
              className="slider"
            />
          </div>
        </div>
      </div>
    </header>
  )
}
